import { api } from './client'
import { formatApiError } from './errors'

/**
 * CloudHSM na conta do tenant: leitura (`src/consoleai/cloudhsm/routes.py`) e gestão de
 * usuários e chaves do HSM (`src/consoleai/cloudhsm/gestao_routes.py`, F6).
 *
 * A regra que a leitura preserva: **regiao nao lida nao e regiao vazia.** A rota
 * devolve `regioes_com_falha` (regiao -> motivo) ao lado de `clusters`, e a tela
 * precisa das duas coisas para nao afirmar "nenhum cluster" sobre uma conta que ela
 * nao conseguiu olhar. Por isso a normalizacao nunca descarta `regioes_com_falha`.
 *
 * A regra que a gestão preserva (G1 da spec F6): a credencial do HSM vai no corpo de cada
 * chamada e em nenhum outro lugar. Ela nunca entra em URL, query string nem chave de cache, e
 * o erro de uma chamada sai sem o corpo enviado (`semCorpo`): o erro do axios guarda o corpo em
 * `config.data`, e o `QueryCache` global guarda o último erro em `window.__lastScreenError`.
 */

export interface HsmCloudHsm {
  hsm_id: string
  estado: string
  mensagem_de_estado: string | null
  zona: string | null
  subnet_id: string | null
  eni_id: string | null
  ip: string | null
}

export interface RetencaoDeBackup {
  tipo: string
  valor: string
}

export interface ClusterCloudHsm {
  cluster_id: string
  regiao: string
  estado: string
  mensagem_de_estado: string | null
  tipo_de_hsm: string | null
  /** `FIPS` ou `NON_FIPS`. */
  modo: string | null
  vpc_id: string | null
  security_group: string | null
  subnets: Record<string, string>
  criado_em: string | null
  retencao_de_backup: RetencaoDeBackup | null
  hsms: HsmCloudHsm[]
}

export interface ListaDeClusters {
  clusters: ClusterCloudHsm[]
  regioes_consultadas: string[]
  regioes_lidas: string[]
  /** Regiao -> motivo (`leitura_nao_concedida` ou `aws_error:<Code>`). */
  regioes_com_falha: Record<string, string>
  regioes_do_tenant: string[]
}

export interface BackupCloudHsm {
  backup_id: string
  estado: string
  criado_em: string | null
  nunca_expira: boolean | null
  regiao_de_origem: string | null
  backup_de_origem: string | null
  copiado_em: string | null
  apagado_em: string | null
}

export interface ListaDeBackups {
  cluster_id: string
  regiao: string
  falha: string | null
  backups: BackupCloudHsm[]
}

export interface EventoDaTrilha {
  quando: string
  hsm: string | null
  /** Como o HSM escreveu — a tela mostra crua. */
  mensagem: string
}

export interface TrilhaDoHsm {
  cluster_id: string
  regiao: string
  log_group: string
  horas: number
  /** `false`: o HSM ainda nao escreveu trilha; `null`: nao deu para saber (ver `falha`). */
  log_group_existe: boolean | null
  eventos: EventoDaTrilha[]
  truncada: boolean
  janela_incompleta: boolean
  falha: string | null
}

const lista = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])
const textos = (v: unknown): string[] => lista<unknown>(v).filter((x): x is string => typeof x === 'string')

export async function listarClusters(tenantId: number, regioes?: string[]): Promise<ListaDeClusters> {
  const params = regioes && regioes.length > 0 ? { regioes: regioes.join(',') } : undefined
  const r = await api.get<ListaDeClusters>(`/v1/tenants/${tenantId}/cloudhsm/clusters`, { params })
  const d = r.data
  const falhas = d?.regioes_com_falha
  return {
    clusters: lista<ClusterCloudHsm>(d?.clusters).map((c) => ({
      ...c,
      subnets: c.subnets && typeof c.subnets === 'object' ? c.subnets : {},
      hsms: lista<HsmCloudHsm>(c.hsms),
    })),
    regioes_consultadas: textos(d?.regioes_consultadas),
    regioes_lidas: textos(d?.regioes_lidas),
    regioes_com_falha: falhas && typeof falhas === 'object' && !Array.isArray(falhas) ? falhas : {},
    regioes_do_tenant: textos(d?.regioes_do_tenant),
  }
}

export async function listarBackups(tenantId: number, clusterId: string, regiao: string): Promise<ListaDeBackups> {
  const r = await api.get<ListaDeBackups>(
    `/v1/tenants/${tenantId}/cloudhsm/clusters/${encodeURIComponent(clusterId)}/backups`,
    { params: { regiao } },
  )
  return {
    cluster_id: r.data?.cluster_id ?? clusterId,
    regiao: r.data?.regiao ?? regiao,
    falha: r.data?.falha ?? null,
    backups: lista<BackupCloudHsm>(r.data?.backups),
  }
}

export async function lerTrilha(
  tenantId: number, clusterId: string, regiao: string, horas: number, limite: number,
): Promise<TrilhaDoHsm> {
  const r = await api.get<TrilhaDoHsm>(
    `/v1/tenants/${tenantId}/cloudhsm/clusters/${encodeURIComponent(clusterId)}/trilha`,
    { params: { regiao, horas, limite } },
  )
  const d = r.data
  return {
    cluster_id: d?.cluster_id ?? clusterId,
    regiao: d?.regiao ?? regiao,
    log_group: d?.log_group ?? '',
    horas: d?.horas ?? horas,
    log_group_existe: typeof d?.log_group_existe === 'boolean' ? d.log_group_existe : null,
    eventos: lista<EventoDaTrilha>(d?.eventos),
    truncada: !!d?.truncada,
    janela_incompleta: !!d?.janela_incompleta,
    falha: d?.falha ?? null,
  }
}

/** Frase da tela para o motivo de uma regiao (ou leitura) que nao foi lida. */
export function explicarMotivo(motivo: string): string {
  if (motivo === 'leitura_nao_concedida') {
    return 'a permissão de leitura do CloudHSM ainda não chegou na role desta conta'
  }
  if (motivo.startsWith('aws_error:')) {
    return `a AWS recusou a leitura (${motivo.slice('aws_error:'.length)})`
  }
  return motivo
}

// ── Gestão de usuários e chaves do HSM (F6) ─────────────────────────────────

export type PapelDoHsm = 'admin' | 'crypto-user'

/** Login no HSM de UMA chamada. Vive só na memória da tela (`sessaoDoHsm.ts`). */
export interface CredencialDoHsm {
  role: PapelDoHsm
  usuario: string
  senha: string
}

export interface UsuarioDoHsm {
  username: string
  /** Como o HSM escreve: `admin`, `crypto-user`, `unactivated-admin`... */
  role: string
  bloqueado: boolean
  cobertura: string | null
}

export interface ChaveDoHsm {
  referencia: string
  /** `null` quando quem listou não vê atributos (o admin só vê a referência). */
  label: string | null
  tipo: string | null
  classe: string | null
  extraivel: boolean | null
  donos: string[]
  compartilhada_com: string[]
}

export interface PaginaDeChaves {
  chaves: ChaveDoHsm[]
  proximo: string | null
  total: number | null
}

export type CurvaEc = 'prime256v1' | 'secp256k1' | 'secp384r1' | 'secp521r1'

/** Do executor: o nome do CLI, e o label sem `-` inicial (o CLI leria como opção). */
export const PADRAO_USUARIO_DO_HSM = /^[A-Za-z0-9_]{1,31}$/
export const PADRAO_LABEL_DE_CHAVE = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,126}$/

/** A senha que o HSM vai gravar (ativar, criar, trocar): a mesma regra do backend. */
export function problemaDaSenhaNova(senha: string): string | null {
  if (senha.length < 8 || senha.length > 32) return 'De 8 a 32 caracteres.'
  // eslint-disable-next-line no-control-regex
  if (/[:\u0000-\u001f\u007f]/.test(senha)) return "Sem ':' e sem caractere de controle."
  return null
}

const gestao = (tenantId: number, clusterId: string) =>
  `/v1/tenants/${tenantId}/cloudhsm/clusters/${encodeURIComponent(clusterId)}`

/** O erro do axios guarda o corpo enviado (com a senha) em `config.data`; ele sai sem. */
function semCorpo(err: unknown): unknown {
  const config = (err as { config?: { data?: unknown } } | null)?.config
  if (config && typeof config === 'object') config.data = undefined
  return err
}

async function enviar<T = unknown>(url: string, corpo: object): Promise<T> {
  try {
    const r = await api.post<{ ok: boolean; data: T }>(url, corpo)
    return r.data?.data as T
  } catch (err) {
    avisarSeDesatualizado(url, err) // D2 (#26396): o botão "Atualizar executor" escuta isto
    throw semCorpo(err)
  }
}

const objeto = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
const texto = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
const nomes = (v: unknown): string[] =>
  lista<unknown>(v).map((x) => texto(objeto(x).username)).filter((x): x is string => !!x)

export async function listarUsuariosDoHsm(
  tenantId: number, clusterId: string, regiao: string,
): Promise<UsuarioDoHsm[]> {
  const r = await api.get<{ ok: boolean; data: unknown }>(`${gestao(tenantId, clusterId)}/usuarios`, {
    params: { regiao },
  })
  return lista<unknown>(objeto(r.data?.data).users).map((u) => {
    const o = objeto(u)
    return {
      username: texto(o.username) ?? '',
      role: texto(o.role) ?? '',
      // O CLI escreve "true"/"false" como texto.
      bloqueado: o.locked === true || o.locked === 'true',
      cobertura: texto(o['cluster-coverage']),
    }
  }).filter((u) => u.username)
}

export async function listarChavesDoHsm(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm,
  pagina: { max_items: number; starting_token?: string | null },
): Promise<PaginaDeChaves> {
  const data = objeto(await enviar(`${gestao(tenantId, clusterId)}/chaves/listar`, {
    regiao, credencial, max_items: pagina.max_items,
    ...(pagina.starting_token ? { starting_token: pagina.starting_token } : {}),
  }))
  const chaves = lista<unknown>(data.matched_keys).map((k) => {
    const o = objeto(k)
    const info = objeto(o['key-info'])
    const attr = objeto(o.attributes)
    return {
      referencia: texto(o['key-reference']) ?? '',
      label: texto(attr.label),
      tipo: texto(attr['key-type']),
      classe: texto(attr.class),
      extraivel: typeof attr.extractable === 'boolean' ? attr.extractable : null,
      donos: nomes(info['key-owners']),
      compartilhada_com: nomes(info['shared-users']),
    }
  }).filter((k) => k.referencia)
  return {
    chaves,
    proximo: texto(data.next_token),
    total: typeof data.total_key_count === 'number' ? data.total_key_count : null,
  }
}

/** `cluster activate`: grava a senha do admin, que ainda não tem senha (sem credencial). */
export function ativarCluster(tenantId: number, clusterId: string, regiao: string, senha: string) {
  return enviar(`${gestao(tenantId, clusterId)}/ativar`, { regiao, senha })
}

export function criarUsuarioDoHsm(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm,
  novo: { username: string; role: PapelDoHsm; senha: string },
) {
  return enviar(`${gestao(tenantId, clusterId)}/usuarios`, { regiao, credencial, ...novo })
}

export function trocarSenhaDoHsm(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm,
  username: string, role: PapelDoHsm, senha: string,
) {
  return enviar(`${gestao(tenantId, clusterId)}/usuarios/${encodeURIComponent(username)}/senha`, {
    regiao, credencial, role, senha,
  })
}

export function apagarUsuarioDoHsm(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm,
  username: string, role: PapelDoHsm, confirmacao: string,
) {
  return enviar(`${gestao(tenantId, clusterId)}/usuarios/${encodeURIComponent(username)}/apagar`, {
    regiao, credencial, role, confirmacao,
  })
}

export type ChaveNova =
  | { algoritmo: 'aes'; label: string; key_length_bytes: 16 | 24 | 32; extractable: boolean }
  | { algoritmo: 'rsa'; public_label: string; private_label: string;
      modulus_size_bits: 2048 | 3072 | 4096; extractable: boolean }
  | { algoritmo: 'ec'; public_label: string; private_label: string; curve: CurvaEc; extractable: boolean }

export function gerarChave(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm, chave: ChaveNova,
) {
  const { algoritmo, ...atributos } = chave
  return enviar(`${gestao(tenantId, clusterId)}/chaves/${algoritmo}`, { regiao, credencial, ...atributos })
}

export function compartilharChave(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm,
  referencia: string, username: string, compartilhar: boolean,
) {
  const acao = compartilhar ? 'compartilhar' : 'descompartilhar'
  return enviar(`${gestao(tenantId, clusterId)}/chaves/${encodeURIComponent(referencia)}/${acao}`, {
    regiao, credencial, username,
  })
}

export function apagarChave(
  tenantId: number, clusterId: string, regiao: string, credencial: CredencialDoHsm,
  referencia: string, confirmacao: string,
) {
  return enviar(`${gestao(tenantId, clusterId)}/chaves/${encodeURIComponent(referencia)}/apagar`, {
    regiao, credencial, confirmacao,
  })
}

const FRASES_DA_GESTAO: Record<string, string> = {
  cloudhsm_management_disabled:
    'A gestão de usuários e chaves do CloudHSM está desligada neste ConsoleAI.',
  tenant_sem_account_id: 'Este tenant ainda não tem conta AWS associada.',
  confirmacao_divergente: 'A confirmação não confere com o que o HSM tem.',
  username_invalido: 'Nome de usuário inválido: até 31 letras, números ou _.',
  key_reference_invalida: 'Referência de chave inválida.',
}

/**
 * Frase da tela para o erro de uma chamada da gestão. O que vem do executor (`{code,
 * mensagem}`: HSM recusou, 1 HSM só, HSM inalcançável) aparece como veio.
 */
export function erroDaGestao(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  const mensagem = texto(objeto(detail).mensagem)
  if (mensagem) return mensagem
  if (typeof detail === 'string') {
    if (detail.startsWith('sem_permissao:')) {
      return `Você não tem a permissão ${detail.slice('sem_permissao:'.length)} neste tenant.`
    }
    if (FRASES_DA_GESTAO[detail]) return FRASES_DA_GESTAO[detail]
  }
  return formatApiError(err, fallback)
}

// --- A: inicialização pela tela (#26221) ---
// O cliente assina o CSR com a CA dele, fora do ConsoleAI, e devolve dois PEM públicos: o
// certificado do HSM e a âncora (customerCA.crt). O backend confere os dois antes de chamar a AWS.

export interface CsrDoCluster {
  cluster_id: string
  regiao: string
  estado: string
  csr: string
}

/** O que a trilha (e a resposta) diz da inicialização: hashes, nunca o PEM. */
export interface ResultadoDaInicializacao {
  parametro: string
  certificado_sha256: string
  ancora_sha256: string
  estado_antes: string
  estado_depois: string
}

/** Estados em que o cartão "Inicializar" aparece. */
export const ESTADOS_DA_INICIALIZACAO = ['UNINITIALIZED', 'INITIALIZE_IN_PROGRESS']

/** `GET .../csr`: só com o cluster em UNINITIALIZED e com o primeiro HSM criado. */
export async function lerCsrDoCluster(tenantId: number, clusterId: string, regiao: string): Promise<CsrDoCluster> {
  const r = await api.get<{ ok: boolean; data: CsrDoCluster }>(`${gestao(tenantId, clusterId)}/csr`, {
    params: { regiao },
  })
  return r.data.data
}

/** `POST .../inicializar`: irreversível, por isso `confirmacao` é o cluster_id digitado. */
export function inicializarCluster(
  tenantId: number, clusterId: string, regiao: string,
  pems: { certificado: string; ancora: string; confirmacao: string },
) {
  return enviar<ResultadoDaInicializacao>(`${gestao(tenantId, clusterId)}/inicializar`, { regiao, ...pems })
}

/** `POST .../ancora`: cluster inicializado à mão; a âncora tem de ter assinado o certificado dele. */
export function gravarAncoraDoCluster(tenantId: number, clusterId: string, regiao: string, ancora: string) {
  return enviar<ResultadoDaInicializacao>(`${gestao(tenantId, clusterId)}/ancora`, { regiao, ancora })
}

/** O comando que o cliente roda com a CA dele para assinar o CSR baixado. */
export function comandoDeAssinatura(clusterId: string): string {
  return [
    `openssl x509 -req -days 3652 -sha256 \\`,
    `  -in ${clusterId}_ClusterCsr.csr \\`,
    `  -CA customerCA.crt -CAkey customerCA.key -CAcreateserial \\`,
    `  -out ${clusterId}_CustomerHsmCertificate.crt`,
  ].join('\n')
}

// --- D1 versao do executor (#26390) ---

/** `antiga` = o executor da conta está numa versão anterior à imagem do catálogo. */
export type SituacaoDoExecutor = 'ausente' | 'antiga' | 'igual' | 'mais_nova'

export interface VersaoLidaDoExecutor {
  /** `null` quando a conta não tem executor (`ausente`). */
  versao_atual: string | null
  /** A tag `AAAA-MM-DD.N` de `CLOUDHSM_EXECUTOR_IMAGE_URI`. */
  versao_esperada: string
  situacao: SituacaoDoExecutor
}

const SITUACOES_DO_EXECUTOR: readonly SituacaoDoExecutor[] = ['ausente', 'antiga', 'igual', 'mais_nova']

/**
 * A versão do executor da conta e a da imagem do catálogo (`GET .../executor`, `cloudhsm:view`).
 * Resposta fora desse formato vira `null`: a tela não afirma versão que não leu.
 */
export async function lerVersaoDoExecutor(
  tenantId: number, clusterId: string, regiao: string,
): Promise<VersaoLidaDoExecutor | null> {
  const r = await api.get<unknown>(`${gestao(tenantId, clusterId)}/executor`, { params: { regiao } })
  const o = objeto(r.data)
  const situacao = SITUACOES_DO_EXECUTOR.find((s) => s === o.situacao)
  const esperada = texto(o.versao_esperada)
  const atual = texto(o.versao_atual)
  if (!situacao || !esperada || (situacao !== 'ausente' && !atual)) return null
  return { versao_atual: atual, versao_esperada: esperada, situacao }
}

// --- B-T/D2 (#26388, #26396) ---
// Ajustes do cluster pelo runner de Terraform (`cloudhsm/expansao.py`): o que o stage do tenant
// permite e, quando não permite, POR QUÊ. O pedido segue o caminho do runner (plan, aprovação,
// MR, apply, entrega) e a tela acompanha pelo cartão do pedido.

/** Um ajuste do cluster: `motivo` vem sempre que `disponivel` é falso. */
export interface AjusteDoCluster {
  disponivel: boolean
  motivo: string | null
  de: Record<string, unknown> | null
  para: Record<string, unknown> | null
  /** US$/h que o ajuste acrescenta; `null` quando não muda custo ou a região não tem preço. */
  custo_hora: number | null
}

export interface PedidoDoCluster {
  id: number
  knob: string
  estado: string
}

export interface AjustesDoCluster {
  knobs: Record<string, AjusteDoCluster>
  /** O pedido de CloudHSM que ocupa o stage agora, se houver. */
  pedido_ativo: PedidoDoCluster | null
}

export const KNOB_HSMS = 'cloudhsm.hsms'

export async function lerAjustes(
  tenantId: number, clusterId: string, regiao: string,
): Promise<AjustesDoCluster> {
  const r = await api.get<AjustesDoCluster>(`${gestao(tenantId, clusterId)}/ajustes`, {
    params: { regiao },
  })
  const knobs = objeto(r.data?.knobs) as Record<string, AjusteDoCluster>
  const ativo = objeto(r.data?.pedido_ativo)
  return {
    knobs: Object.fromEntries(Object.entries(knobs).filter(
      ([, a]) => a && typeof a === 'object' && typeof a.disponivel === 'boolean')),
    pedido_ativo: typeof ativo.id === 'number'
      ? { id: ativo.id, knob: texto(ativo.knob) ?? '', estado: texto(ativo.estado) ?? '' }
      : null,
  }
}

async function pedirAjuste(url: string, regiao: string): Promise<number> {
  try {
    const r = await api.post<{ pedido_id: number }>(url, { regiao })
    return r.data.pedido_id
  } catch (err) {
    throw semCorpo(err)
  }
}

/** Abre o pedido de um HSM a mais; devolve o número do pedido do runner. */
export function adicionarHsm(tenantId: number, clusterId: string, regiao: string): Promise<number> {
  return pedirAjuste(`${gestao(tenantId, clusterId)}/adicionar-hsm`, regiao)
}

export const KNOB_EXECUTOR = 'cloudhsm.executor_image'

/** Abre o pedido que leva o executor à imagem do catálogo (`CLOUDHSM_EXECUTOR_IMAGE_URI`). */
export function atualizarExecutor(tenantId: number, clusterId: string, regiao: string): Promise<number> {
  return pedirAjuste(`${gestao(tenantId, clusterId)}/atualizar-executor`, regiao)
}

/**
 * Evento da janela quando uma ação da gestão volta `executor_desatualizado` (o executor da conta
 * não conhece a ação): o cartão do cluster mostra o botão "Atualizar executor" na hora, sem
 * esperar a releitura da versão. `detail.url` diz de que cluster veio.
 */
export const EVENTO_EXECUTOR_DESATUALIZADO = 'cloudhsm:executor-desatualizado'

function avisarSeDesatualizado(url: string, err: unknown): void {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  if (objeto(detail).code !== 'executor_desatualizado' || typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(EVENTO_EXECUTOR_DESATUALIZADO, { detail: { url } }))
}

/** A URL de uma chamada da gestão é deste cluster? */
export function urlDoCluster(url: string, tenantId: number, clusterId: string): boolean {
  return url.startsWith(`${gestao(tenantId, clusterId)}/`)
}
